import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './NewProjects.module.css';
import { projectsConfig } from '../sections/projectsConfig';
import { loadThumbnail, loadProjectGallery } from '../sections/imageLoader';

/* ────────────────────────────────────────────
   NewProjects — Cinema-strip portfolio gallery
   ──────────────────────────────────────────── */

export default function NewProjects() {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const modalRef = useRef(null);
  const prefersReduced = useRef(false);

  const [projects, setProjects] = useState([]);
  const [visibleCards, setVisibleCards] = useState(() => new Set());

  // Modal state
  const [selected, setSelected] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  /* ── reduced-motion check ── */
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReduced.current = mq.matches;
  }, []);

  /* ── 1. Load thumbnails ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await Promise.all(
        projectsConfig.map(async (p) => {
          try {
            const thumb = await loadThumbnail(p.folderName, p.thumbnailIndex);
            return { ...p, thumbnail: thumb };
          } catch {
            return { ...p, thumbnail: null };
          }
        })
      );
      if (!cancelled) setProjects(list);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── 2. Reveal cards on scroll ── */
  useEffect(() => {
    const cards = cardRefs.current.filter(Boolean);
    if (!cards.length) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            const delay = prefersReduced.current ? 0 : (idx % 2) * 120;
            setTimeout(() => {
              setVisibleCards((prev) => {
                const next = new Set(prev);
                next.add(idx);
                return next;
              });
            }, delay);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    cards.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [projects]);

  /* ── 3. Open project modal ── */
  const openProject = useCallback(async (project) => {
    setSelected(project);
    setGallery([]);
    setGalleryLoading(true);
    document.body.style.overflow = 'hidden';

    try {
      const imgs = await loadProjectGallery(
        project.folderName,
        project.imageCount
      );
      setGallery(imgs);
    } catch {
      setGallery([]);
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  /* ── 4. Close modal ── */
  const closeModal = useCallback(() => {
    setSelected(null);
    setGallery([]);
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  /* ── 5. Lightbox ── */
  const openLightbox = useCallback((idx) => {
    setLightboxIdx(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const lightboxPrev = useCallback(() => {
    setLightboxIdx((prev) => (prev <= 0 ? gallery.length - 1 : prev - 1));
  }, [gallery.length]);

  const lightboxNext = useCallback(() => {
    setLightboxIdx((prev) => (prev >= gallery.length - 1 ? 0 : prev + 1));
  }, [gallery.length]);

  /* ── 6. Keyboard navigation ── */
  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxOpen) {
        if (e.key === 'Escape') closeLightbox();
        else if (e.key === 'ArrowLeft') lightboxNext(); // RTL: left = next
        else if (e.key === 'ArrowRight') lightboxPrev(); // RTL: right = prev
      } else if (selected) {
        if (e.key === 'Escape') closeModal();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, selected, closeLightbox, closeModal, lightboxNext, lightboxPrev]);

  /* ── 7. Click outside modal ── */
  const handleBackdropClick = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
      }
    },
    [closeModal]
  );

  return (
    <section
      id="projects"
      ref={sectionRef}
      className={styles.section}
      dir="rtl"
    >
      {/* Header */}
      <header className={styles.header}>
        <span className={styles.eyebrow}>
          &mdash; {'\u05D4\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD'} &mdash;
        </span>
        <h2 className={styles.title}>
          {'\u05D1\u05EA\u05D9\u05DD \u05E9\u05EA\u05DB\u05E0\u05E0\u05D5, \u05D7\u05DC\u05D5\u05DE\u05D5\u05EA \u05E9\u05D4\u05D2\u05E9\u05DE\u05E0\u05D5'}
        </h2>
        <p className={styles.lead}>
          {'\u05DB\u05DC \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8 \u05DE\u05EA\u05D7\u05D9\u05DC \u05D1\u05E9\u05D9\u05D7\u05D4 \u05D5\u05E0\u05D2\u05DE\u05E8 \u05D1\u05D1\u05D9\u05EA \u05E9\u05DE\u05E8\u05D2\u05D9\u05E9 \u05D1\u05D3\u05D9\u05D5\u05E7 \u05E0\u05DB\u05D5\u05DF.'}
        </p>
      </header>

      {/* Masonry Grid */}
      <div className={styles.grid}>
        {projects.map((project, i) => (
          <article
            key={project.id}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            data-index={i}
            className={[
              styles.card,
              visibleCards.has(i) ? styles.cardVisible : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => openProject(project)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openProject(project);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`${project.title} - ${project.subtitle}`}
          >
            <div className={styles.cardImageWrap}>
              {project.thumbnail ? (
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className={styles.cardImage}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className={styles.cardSkeleton} />
              )}
              <div className={styles.cardOverlay}>
                <span className={styles.cardCategory}>{project.category}</span>
                <h3 className={styles.cardName}>{project.title}</h3>
                <span className={styles.cardLocation}>{project.subtitle}</span>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* ── Modal ── */}
      {selected && (
        <div
          className={styles.modalBackdrop}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label={selected.title}
        >
          <div ref={modalRef} className={styles.modal}>
            {/* Close button */}
            <button
              className={styles.modalClose}
              onClick={closeModal}
              aria-label="Close"
              type="button"
            >
              &times;
            </button>

            {/* Hero image */}
            <div className={styles.modalHero}>
              {selected.thumbnail && (
                <img
                  src={gallery[0] || selected.thumbnail}
                  alt={selected.title}
                  className={styles.modalHeroImg}
                />
              )}
              <div className={styles.modalHeroOverlay}>
                <span className={styles.modalCategory}>
                  {selected.category}
                </span>
                <h2 className={styles.modalTitle}>{selected.title}</h2>
                <span className={styles.modalSubtitle}>
                  {selected.subtitle}
                </span>
              </div>
            </div>

            {/* Gallery strip */}
            <div className={styles.modalGallery}>
              {galleryLoading && (
                <div className={styles.galleryLoading}>
                  <div className={styles.spinner} />
                </div>
              )}
              {!galleryLoading && gallery.length > 0 && (
                <div className={styles.galleryStrip}>
                  {gallery.map((src, idx) => (
                    <button
                      key={idx}
                      className={styles.galleryThumb}
                      onClick={() => openLightbox(idx)}
                      type="button"
                      aria-label={`${selected.title} image ${idx + 1}`}
                    >
                      <img
                        src={src}
                        alt={`${selected.title} ${idx + 1}`}
                        className={styles.galleryThumbImg}
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightboxOpen && gallery.length > 0 && (
        <div
          className={styles.lightbox}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <div
            className={styles.lightboxContent}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              className={styles.lightboxClose}
              onClick={closeLightbox}
              aria-label="Close lightbox"
              type="button"
            >
              &times;
            </button>

            {/* Navigation */}
            <button
              className={[styles.lightboxArrow, styles.lightboxArrowRight].join(
                ' '
              )}
              onClick={lightboxPrev}
              aria-label="Previous image"
              type="button"
            >
              &#8250;
            </button>
            <button
              className={[styles.lightboxArrow, styles.lightboxArrowLeft].join(
                ' '
              )}
              onClick={lightboxNext}
              aria-label="Next image"
              type="button"
            >
              &#8249;
            </button>

            {/* Main image */}
            <img
              src={gallery[lightboxIdx]}
              alt={`${selected.title} ${lightboxIdx + 1}`}
              className={styles.lightboxImg}
            />

            {/* Progress dots */}
            <div className={styles.lightboxDots}>
              {gallery.map((_, idx) => (
                <button
                  key={idx}
                  className={[
                    styles.lightboxDot,
                    idx === lightboxIdx ? styles.lightboxDotActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIdx(idx);
                  }}
                  aria-label={`Go to image ${idx + 1}`}
                  type="button"
                />
              ))}
            </div>

            {/* Thumbnail strip */}
            <div className={styles.lightboxThumbs}>
              {gallery.map((src, idx) => (
                <button
                  key={idx}
                  className={[
                    styles.lightboxThumb,
                    idx === lightboxIdx ? styles.lightboxThumbActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIdx(idx);
                  }}
                  type="button"
                  aria-label={`View image ${idx + 1}`}
                >
                  <img
                    src={src}
                    alt=""
                    className={styles.lightboxThumbImg}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
